import { useMutation, useQuery } from "@apollo/client"
import { useToast } from "@/hooks/use-toast"
import { useAuthContext } from "@/contexts/AuthContext"
import {
  LOGIN_MUTATION,
  REGISTER_MUTATION,
  FORGOT_PASSWORD_MUTATION,
  GET_USER_INFO,
  ADDRESS_CONFIRM_MUTATION,
} from "../queries/auth"
import type {
  UserLoginInput,
  UserRegisterInput,
  LoginPayload,
  UserPasswdRecoverInput,
  RegisterPayload,
  UserInfo,
  AddressConfirmInput,
  AddressConfirmPayload,
} from "../types"

export const useLogin = () => {
  const { toast } = useToast()
  const { login: authLogin } = useAuthContext()

  const [loginMutation, { loading, error }] = useMutation<
    { UserLogin: LoginPayload },
    { UserLoginInput: UserLoginInput }
  >(LOGIN_MUTATION, {
    onCompleted: (data) => {
      if (data.UserLogin.status.success && data.UserLogin.token) {
        authLogin(data.UserLogin.token)
        toast({
          title: "¡Bienvenido!",
          variant: "success",
          description: "Has iniciado sesión correctamente.",
        })
      } else {
        toast({
          title: "Error",
          description: data.UserLogin.status.detail || "Credenciales incorrectas.",
          variant: "destructive",
        })
      }
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Ha ocurrido un error. Inténtalo de nuevo.",
        variant: "destructive",
      })
    },
  })

  const login = async (username: string, passwd: string) => {
    try {
      const result = await loginMutation({
        variables: { UserLoginInput: { username, passwd, type: "EMAIL" } },
      })
      return result.data?.UserLogin
    } catch (err) {
      console.log(err)
      throw err
    }
  }

  return { login, loading, error }
}

export const useRegister = () => {
  const { toast } = useToast()

  const [registerMutation, { loading, error }] = useMutation<
    { UserRegister: RegisterPayload },
    { UserRegisterInput: UserRegisterInput }
  >(REGISTER_MUTATION, {
    onCompleted: (data) => {
      if (data.UserRegister.status.success) {
        toast({
          title: "¡Cuenta creada!",
          description: `Te has registrado correctamente, ahora puedes ingresar al sitio con tu cuenta. Tu código de jugador es: "${data?.UserRegister.person.code}"`,
          variant: "success",
        })
      } else {
        toast({
          title: "Error",
          description: data.UserRegister.status.detail || "No se pudo crear la cuenta.",
          variant: "destructive",
        })
      }
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Ha ocurrido un error. Inténtalo de nuevo.",
        variant: "destructive",
      })
    },
  })

  const register = async (input: UserRegisterInput) => {
    try {
      const result = await registerMutation({
        variables: { UserRegisterInput: input },
      })
      return result.data?.UserRegister
    } catch (err) {
      console.log(err)
      throw err
    }
  }

  return { register, loading, error }
}

export const useForgotPassword = () => {
  const { toast } = useToast()

  const [forgotPasswordMutation, { loading, error }] = useMutation<
    {
      UserPasswdRecover: {
        status: { success: boolean; name: string; detail: string }
      }
    },
    { UserPasswdRecoverInput: UserPasswdRecoverInput }
  >(FORGOT_PASSWORD_MUTATION, {
    onCompleted: (data) => {
      if (data.UserPasswdRecover.status.success) {
        toast({
          title: "Email enviado",
          variant: "success",
          description: "Revisa tu correo para restablecer tu contraseña.",
        })
      } else {
        toast({
          title: "Error",
          description: data.UserPasswdRecover.status.detail || "No se pudo enviar el email.",
          variant: "destructive",
        })
      }
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Ha ocurrido un error. Inténtalo de nuevo.",
        variant: "destructive",
      })
    },
  })

  const forgotPassword = async (email: string) => {
    try {
      const result = await forgotPasswordMutation({
        variables: { UserPasswdRecoverInput: { search: email } },
      })
      return result.data?.UserPasswdRecover
    } catch (err) {
      console.log(err)
      throw err
    }
  }

  return { forgotPassword, loading, error }
}

export const useCurrentUser = () => {
  const token = localStorage.getItem("authTokenMyL")

  const { data, loading, error, refetch } = useQuery<{ UserInfo: UserInfo }>(GET_USER_INFO, {
    errorPolicy: "ignore",
    skip: !localStorage.getItem("authTokenMyL"),
  })

  return {
    userInfo: data?.UserInfo,
    user: data?.UserInfo?.User,
    person: data?.UserInfo?.User?.Person,
    hasActiveStore: data?.UserInfo?.User?.Person?.Store?.active === 1,
    loading,
    error,
    refetch,
  }
}

export const useAddressConfirm = () => {
  const { toast } = useToast()

  const [addressConfirmMutation, { loading, error }] = useMutation<
    { AddressConfirm: AddressConfirmPayload },
    { input: AddressConfirmInput }
  >(ADDRESS_CONFIRM_MUTATION, {
    onError: () => {
      toast({
        title: "Error",
        description: "Ha ocurrido un error. Inténtalo de nuevo.",
        variant: "destructive",
      })
    },
  })

  const confirmAddress = async (input: AddressConfirmInput) => {
    const result = await addressConfirmMutation({ variables: { input } })
    return result.data?.AddressConfirm
  }

  return { confirmAddress, loading, error }
}

export const useLogout = () => {
  const { logout: authLogout } = useAuthContext()

  return { logout: authLogout }
}
